import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { X, Edit3, AlertCircle } from 'lucide-react';
import { isWhiteLabel } from '../../config/tenantConfig';
import { UpdateBasket } from '../../types';
import { validateStockmockUrl } from '../../utils/stockmockUrl';

interface EditBasketDialogProps {
  basket: {
    basket_id: string;
    basket_name: string;
    description?: string;
    initial_capital?: number;
    stockmock_url?: string;
  };
  onClose: () => void;
  onSubmit: (basketId: string, updates: UpdateBasket) => Promise<void>;
}

const EditBasketDialog: React.FC<EditBasketDialogProps> = ({ basket, onClose, onSubmit }) => {
  const wl = isWhiteLabel();
  const [formData, setFormData] = useState({
    basket_name: basket.basket_name || '',
    description: basket.description || '',
    initial_capital: basket.initial_capital ?? 100000,
    stockmock_url: basket.stockmock_url || '',
  });
  const [capitalInputValue, setCapitalInputValue] = useState(String(basket.initial_capital ?? 100000));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stockmockUrlError, setStockmockUrlError] = useState<string | null>(null);

  const handleCapitalChange = (rawValue: string) => {
    setCapitalInputValue(rawValue);
    if (rawValue === '') return;
    const parsed = Number(rawValue);
    if (!Number.isNaN(parsed)) {
      setFormData(prev => ({ ...prev, initial_capital: parsed }));
    }
  };

  const handleCapitalBlur = () => {
    const parsed = Number(capitalInputValue.trim());
    const finalValue = capitalInputValue.trim() === '' || Number.isNaN(parsed) ? 0 : parsed;
    setFormData(prev => ({ ...prev, initial_capital: finalValue }));
    setCapitalInputValue(String(finalValue));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.basket_name.trim()) {
      setError('Basket name is required');
      return;
    }

    if (formData.initial_capital < 10000) {
      setError('Initial capital must be at least ₹10,000');
      return;
    }

    const urlCheck = validateStockmockUrl(formData.stockmock_url);
    if (urlCheck.error) {
      setStockmockUrlError(urlCheck.error);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updates: UpdateBasket = {
        basket_name: formData.basket_name.trim(),
        description: formData.description.trim() || undefined,
        initial_capital: formData.initial_capital,
        // Send the field whenever the user touched it — an empty string
        // explicitly clears the previously stored value (backend treats
        // empty string as clear).
        stockmock_url: urlCheck.value ?? '',
      };
      await onSubmit(basket.basket_id, updates);
    } catch (err: any) {
      setError(err.message || 'Failed to update basket');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <FocusedDialog
        ariaLabel="Edit basket"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col"
      >
        <Card className="border-0 shadow-none flex flex-col min-h-0">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5" />
                Edit Basket
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="overflow-y-auto min-h-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Basket Name *</label>
                <Input
                  value={formData.basket_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, basket_name: e.target.value }))}
                  placeholder="e.g., Conservative Income Strategies"
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Initial Capital *</label>
                <Input
                  type="number"
                  value={capitalInputValue}
                  onChange={(e) => handleCapitalChange(e.target.value)}
                  onBlur={handleCapitalBlur}
                  placeholder="100000"
                  disabled={isSubmitting}
                  min="10000"
                  step="1000"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Starting capital for this basket (minimum ₹10,000)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of your basket..."
                  disabled={isSubmitting}
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 ${wl ? 'focus:ring-zebu-500 focus:border-zebu-500' : 'focus:ring-blue-500 focus:border-blue-500'}`}
                />
              </div>

              {/* StockMock URL — owner-only reference. Clear by emptying the field. */}
              <div>
                <label className="block text-sm font-medium mb-2">StockMock URL (Optional)</label>
                <Input
                  type="url"
                  value={formData.stockmock_url}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, stockmock_url: e.target.value }));
                    if (stockmockUrlError) setStockmockUrlError(null);
                  }}
                  placeholder="https://www.stockmock.in/#!/share/..."
                  disabled={isSubmitting}
                />
                {stockmockUrlError && (
                  <p className="text-xs text-red-600 mt-1">{stockmockUrlError}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Reference link visible only to you. Empty the field to remove.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.basket_name.trim() || formData.initial_capital < 10000}
                  className="flex-1"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </FocusedDialog>
    </div>
  );
};

export default EditBasketDialog;
